import { JSX, useState } from 'react';
import { Clock, Copy, Check } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import Icons from '../../icons/icons';
import { cn, formatDuration } from '../../lib/utils';
import {
  AgenticInvocation,
  AgenticMcpInvocation,
  AgenticToolInvocation,
  DataModel,
  HttpProcess,
} from '../../types/tree-view-types';
import {
  getAllTokensCount,
  getExceptionType,
  getTokens,
} from '../../utils/tree-view-utils';
import Separator from '../system/separator';

interface NodeLabelProps {
  node: DataModel;
  isFirst?: boolean;
  errorClickHandler: (node: DataModel, e: React.MouseEvent) => void;
  mcpClickHandler: (node: DataModel, e: React.MouseEvent) => void;
  metadataClickHandler: (node: DataModel, property: string, e: React.MouseEvent) => void;
  hasError?: boolean;
  selectedNodeId?: string | null;
  tokenCount?: string;
  tokenTooltip?: string;
  onWorkflowNameClick?: (workflowName: string) => void;
}

const isUrlOrPath = (str: string) => {
  const trimmed = str.trim();
  return (
    (trimmed.includes('/') || trimmed.includes('\\') || trimmed.toLowerCase().startsWith('www.')) &&
    !trimmed.includes(' ')
  );
};

// extract input from event data.input
const getInputValue = (node: DataModel): string | null => {
  const events = node.events as any[];
  const inputEvent = events?.find((event) => event.name === 'data.input');
  if (!inputEvent) return null;

  const InputValue = inputEvent.attributes?.input;
  return typeof InputValue === 'string' ? InputValue : InputValue ? JSON.stringify(InputValue) : null;
};

// extract response from event data.output
const getOutputValue = (node: DataModel): string | null => {
  const events = node.events as any[];
  const outputEvent = events?.find((event) => event.name === 'data.output');
  if (!outputEvent) return null;

  const OutputValue = outputEvent.attributes?.response;
  return typeof OutputValue === 'string' ? OutputValue : OutputValue ? JSON.stringify(OutputValue) : null;
};




const NodeLabel: React.FC<NodeLabelProps> = ({
  node,
  isFirst,
  errorClickHandler,
  mcpClickHandler,
  metadataClickHandler,
  hasError,
  selectedNodeId,
  tokenCount,
  tokenTooltip,
  onWorkflowNameClick,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (text: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // prevent opening json viewer
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const invokesIcon = Icons.Invokes;
  const checkCircleOutlineIcon = Icons.CheckCircleOutline;
  const ErrorIcon = Icons.ErrorIcon('fill-[#F26C74] w-4 h-4');
  const { spanType, spanSubType, label, attributes, events, isMCPSpan } = node;

  // Normalize type for comparison
  const spanTypeLowerCase = spanType?.toLowerCase() || '';

  // Normalize subtype for comparison
  const subtype = spanSubType || '';

  // Error badge label: the exception type, the status message, or the status
  // description fallback. For the status-message case we show a generic
  // "exception" label and put the (often long) message in a tooltip; the other
  // cases render their value inline without a tooltip.
  const errorLabel = getExceptionType(node);
  const ErrorBadge = errorLabel ? (
    <div
      className="group flex items-center justify-center gap-0.5 rounded-2xl bg-[#F26C7433] py-[1px] pl-1 pr-2 leading-3 tracking-[2%]"
      onClick={(e) => errorClickHandler(node, e)}
    >
      <span> {ErrorIcon}</span>
      {errorLabel.kind === 'status' ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-xs font-normal leading-[11px] tracking-[2%] text-[#F9A8AD] group-hover:text-white">
                exception
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-[320px] break-words">
              {errorLabel.value}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <p className="text-xs font-normal leading-[11px] tracking-[2%] text-[#F9A8AD] group-hover:text-white">
          {errorLabel.value}
        </p>
      )}
    </div>
  ) : null;

  const PrefixLabel = (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          'font-worksans flex items-center gap-1.5 text-sm leading-[1.4]',
          selectedNodeId === node.id
            ? 'font-semibold text-[#EBEFF1]'
            : 'text-[#C1C9CF] font-normal'
        )}
      >
        {spanType}
        {spanSubType ? (
          <div className="flex items-center gap-1.5">
            <Separator orientation="vertical" className="h-4" />
            {spanSubType}
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );

  // root span for traces fact.
  const durationAndErrorBadge: JSX.Element | null =
    node.spanType === 'fact' ? null : isFirst ? (
      <div className="flex items-center gap-1.5 pr-1">
        <div className="flex items-center justify-center gap-2.5">
          <div className="flex h-6 items-center gap-1 rounded-full bg-gray-700 px-2 py-0.5 leading-4">
            <Clock size={16} className="text-[#C1C9CF]" />
            <span className="text-xs text-[#C1C9CF]">
              {formatDuration(node.duration)}
            </span>
          </div>

          <span className="text-xs text-[#8C9EA1]">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-[#8C9EA1]">
                    {tokenCount || getAllTokensCount(node)}
                  </span>
                </TooltipTrigger>
                {tokenTooltip && <TooltipContent>{tokenTooltip}</TooltipContent>}
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
        {hasError && ErrorBadge}
      </div>
    ) : (
      <div className="flex items-center gap-1.5 pr-1">
        <div className="flex h-6 items-center justify-center gap-2.5">
          <span className="text-xs text-[#8C9EA1]">{formatDuration(node.duration)}</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-[#8C9EA1]">
                  {node.tokenCount ?? tokenCount ?? getTokens(node)}
                </span>
              </TooltipTrigger>
              {(node.tokenTooltip ?? tokenTooltip) && (
                <TooltipContent>
                  {node.tokenTooltip ?? tokenTooltip}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
        {hasError && ErrorBadge}
      </div>
    );

  // fact node
  if (spanTypeLowerCase === 'fact') {
    return (
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            'text-base font-bold',
            selectedNodeId === node.id
              ? 'text-[#EBEFF1]'
              : 'text-[#C1C9CF]'
          )}
        >
          {label}
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-[#8C9EA1]">
                {tokenCount}
              </span>
            </TooltipTrigger>
            {tokenTooltip && <TooltipContent>{tokenTooltip}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  const handleWorkflowNameClick = (e: React.MouseEvent) => {
    if (onWorkflowNameClick && node.workflowName) {
      e.stopPropagation();
      onWorkflowNameClick(node.workflowName);
    }
  };

  // Handle generic/custom/workflow --> just show span label
  if (['custom', 'generic', 'workflow'].includes(spanTypeLowerCase)) {
    let displayLabel: React.ReactNode = label;
    if (spanTypeLowerCase === 'workflow' && node.workflowName) {
      displayLabel = (
        <>
          workflow:{' '}
          <span
            className={onWorkflowNameClick ? "cursor-pointer hover:underline underline-offset-2" : ""}
            onClick={handleWorkflowNameClick}
          >
            {node.workflowName}
          </span>
          {node.traceId ? ` | Trace: ${node.traceId}` : null}
        </>
      );
    } else if (spanTypeLowerCase === 'workflow' && !label.startsWith('workflow')) {
      displayLabel = `workflow | ${label}`;
    }

    return (
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'font-worksans',
            selectedNodeId === node.id
              ? 'font-semibold text-[#EBEFF1]'
              : 'font-normal text-[#C1C9CF]'
          )}
        >
          {displayLabel}
        </span>
        {durationAndErrorBadge}
      </div>
    );
  }

  switch (spanTypeLowerCase) {
    case 'agentic.tool.invocation': {
      const entities = (attributes as AgenticToolInvocation)?.entity || [];

      const agent = entities.find((e) =>
        e.type?.toLowerCase().startsWith('agent')
      );

      const tool = entities.find((e) =>
        e.type?.toLowerCase().startsWith('tool')
      );

      const description = tool?.description;

      return agent?.name && tool?.name ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            {PrefixLabel}
            {durationAndErrorBadge}
          </div>
          <div className="flex items-center gap-2 font-mono text-[13px]">
            <span className="rounded-[3px] bg-[#43B0CD1A] px-[3px] leading-[1.4] text-[#43B0CD]">
              {agent?.name}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{invokesIcon('')}</span>
                </TooltipTrigger>
                <TooltipContent>invokes</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="rounded-[3px] bg-[#1212127A] px-[3px] leading-[1.4] text-[#FFCB78]">
              {tool?.name}
            </span>
            {/* show description if it exists and not same as name */}
            {description && description.trim() && description.trim().toLowerCase() !== tool.name.trim().toLowerCase() && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {isUrlOrPath(description) ? (
                      <span
                        onClick={(e) => metadataClickHandler(node, 'description', e)}
                        className="group inline-flex items-center gap-1 ml-1 cursor-pointer"
                      >
                        <span
                          className="inline-block min-w-0 max-w-[300px] text-form-input-disabled text-xs group-hover:text-body-primary truncate select-text pointer-events-none"
                        >
                          {description}
                        </span>
                        <button
                          onClick={(e) => handleCopy(description, e)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-form-input-disabled hover:text-body-primary focus:outline-none flex items-center justify-center"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-[#257C9D]" />
                          ) : (
                            <Copy className="w-4 h-4 text-[#8C9EA1] hover:text-body-primary" />
                          )}
                        </button>
                      </span>
                    ) : (
                      <span
                        onClick={(e) => metadataClickHandler(node, 'description', e)}
                        className="group inline-flex ml-1 cursor-pointer"
                      >
                        <span className="inline-block min-w-0 max-w-[300px] text-form-input-disabled text-xs group-hover:text-body-primary truncate select-text pointer-events-none">
                          {description}
                        </span>
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[900px] w-auto break-words text-body-primary select-text">
                    {description}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isMCPSpan && (
              <span
                onClick={(e) => mcpClickHandler(node, e)}
                className="-ml-[4px]"
              >
                {Icons.McpIcon()}
              </span>
            )}
          </div>
        </div>
      ) : (
        label
      );
    }

    case 'agentic.turn': {
      const inputValue = getInputValue(node);
      const outputValue = getOutputValue(node);

      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5">
            {PrefixLabel}
            {durationAndErrorBadge}
          </div>

          <div className="flex flex-col gap-0.5 text-xs text-body-subtle">
            {inputValue && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      onClick={(e) => metadataClickHandler(node, 'data.input', e)}
                      className="group flex items-center gap-1 cursor-pointer w-fit"
                    >
                      <span className='text-form-input-disabled group-hover:text-body-primary'>Input:</span>
                      <span
                        className="truncate max-w-[500px] group-hover:text-body-primary text-subtle pointer-events-none"
                      >
                        {inputValue}
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[900px] w-auto break-words text-body-primary select-text">
                    {inputValue}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {outputValue && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      onClick={(e) => metadataClickHandler(node, 'data.output', e)}
                      className="group flex items-center gap-1 cursor-pointer w-fit"
                    >
                      <span className='text-form-input-disabled group-hover:text-body-primary'>Output:</span>
                      <span
                        className="truncate max-w-[500px] group-hover:text-body-primary text-subtle pointer-events-none"
                      >
                        {outputValue}
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[900px] w-auto break-words text-body-primary select-text">
                    {outputValue}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

        </div>
      );
    }

    case 'agentic.mcp.invocation': {
      const entities = (attributes as AgenticMcpInvocation)?.entity || [];

      const toolName = entities[0]?.name || '';
      const agentName = entities[1]?.name || '';

      return toolName && agentName ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            {PrefixLabel}
            {durationAndErrorBadge}
          </div>
          <div className="flex items-center gap-2 font-mono text-[13px]">
            <span className="rounded-[3px] bg-[#43B0CD1A] px-[3px] leading-[1.4] text-[#43B0CD]">
              {agentName}
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{invokesIcon('')}</span>
                </TooltipTrigger>
                <TooltipContent>invokes</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="rounded-[3px] bg-[#43B0CD1A] px-[3px] leading-[1.4] text-[#43B0CD]">
              {toolName}
            </span>
          </div>
        </div>
      ) : (
        label
      );
    }

    case 'agentic.invocation': {
      const entities = (attributes as AgenticInvocation)?.entity || [];

      const { name } = entities.find((e) => e.name) || {};

      return name ? (
        <div className="flex items-center gap-1.5 text-sm">
          {PrefixLabel}
          <Separator orientation="vertical" className="h-4" />
          <span className="rounded-[3px] bg-[#43B0CD1A] px-[3px] leading-[1.4] text-[#43B0CD]">
            {name}
          </span>
          {durationAndErrorBadge}
        </div>
      ) : (
        label
      );
    }

    case 'inference.framework':
    case 'inference': {
      const entities = (attributes as AgenticToolInvocation)?.entity || [];

      const modelName = entities[1]?.name || '';

      const finishReason = events.find((e) => e.attributes.finish_reason || '');
      const finishType = events.find((e) => e.attributes.finish_type || '');

      const finishReasonString = finishReason?.attributes.finish_reason;
      const finishTypeString = finishType?.attributes.finish_type;

      return modelName && finishReasonString ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            {PrefixLabel}
            {durationAndErrorBadge}
          </div>
          <div className="flex items-center gap-2 font-mono text-[13px]">
            <span className="rounded-[3px] bg-[#1212127A] px-[3px] leading-[1.4] text-[#D086EC]">
              {modelName}
            </span>
            <span className="text-[#8C9EA1]">with</span>
            <span className="rounded-[3px] bg-[#1212127A] px-[3px] leading-[1.4] text-[#FFCB78]">
              {finishReasonString}
            </span>
            {finishTypeString === 'success' && checkCircleOutlineIcon('')}
          </div>
        </div>
      ) : (
        label
      );
    }

    case 'inference.modelapi':
      return (
        <div className="flex items-center gap-1.5">
          {PrefixLabel}
          <span className="bg-[#43B0CD1A] text-[#43B0CD]">{label}</span>
          {durationAndErrorBadge}
        </div>
      );

    case 'http.process': {
      const entities = (attributes as HttpProcess)?.entity || [];

      const { method, route } = entities.find((e) => e.method && e.route) || {};

      return method && route ? (
        <div className="flex items-center gap-1.5">
          {PrefixLabel}
          {method} {route}
          {durationAndErrorBadge}
        </div>
      ) : (
        label
      );
    }

    case 'retrieval': {
      const entities = (attributes as AgenticToolInvocation)?.entity || [];

      const { name, type } = entities[0] || {};

      return name && type ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            {PrefixLabel}
            {durationAndErrorBadge}
          </div>
          <div className="flex items-center gap-2 font-mono text-[13px]">
            <span className="rounded-[3px] bg-[#1212127A] px-[3px] leading-[1.4] text-[#D086EC]">
              {name}
            </span>
            <span className="text-[#8C9EA1]">on</span>
            <span className="rounded-[3px] bg-[#1212127A] px-[3px] leading-[1.4] text-[#FFCB78]">
              {type}
            </span>
            {checkCircleOutlineIcon('')}
          </div>
        </div>
      ) : (
        label
      );
    }

    // Fallback --> type | subtype or just type
    default: {
      return subtype ? (
        <div className="flex items-center gap-1.5">
          <div
            className={cn(
              'font-worksans flex items-center gap-1.5',
              selectedNodeId === node.id
                ? 'font-semibold text-[#EBEFF1]'
                : 'text-[#C1C9CF] font-normal'
            )}
          >
            <span>{spanType}</span>
            <Separator orientation="vertical" className="h-4" />
            <span>{subtype}</span>
          </div>
          {durationAndErrorBadge}
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'font-worksans',
              selectedNodeId === node.id
                ? 'font-semibold text-[#EBEFF1]'
                : 'text-[#C1C9CF] font-normal'
            )}
          >
            {spanType}
          </span>
          {durationAndErrorBadge}
        </div>
      );
    }
  }
};

export default NodeLabel;
